import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';

import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import HomeIcon from '@mui/icons-material/Home';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';

export default function Navbar() {
  // Authentication context
  const { user, logout, isAuthenticated, isStaff, isSuperuser } = useAuth();
  const navigate = useNavigate();

  // Dark mode state (saved in localStorage)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem('theme') === 'dark';
  });

  // States for navbar behavior and mobile menus
  const [isVisible, setIsVisible] = useState(true);
  const [scrollPos, setScrollPos] = useState(0);
  const [isIdle, setIsIdle] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  // Determine if the user has admin access (staff or superuser)
  const [hasAdminAccess, setHasAdminAccess] = useState(false);
  useEffect(() => {
    if (isAuthenticated) {
      const staffStatus = typeof isStaff === 'function' ? isStaff() : isStaff;
      const superuserStatus = typeof isSuperuser === 'function' ? isSuperuser() : isSuperuser;
      setHasAdminAccess(staffStatus || superuserStatus);
    } else {
      setHasAdminAccess(false);
    }
  }, [isAuthenticated, isStaff, isSuperuser]);

  // Define additional navigation links
  const navLinks = [
    { name: 'Documentation', path: '/documentation', show: true, external: false },
    { name: 'Tools', path: '/tools', show: true, external: false },
    { name: 'Dashboard', path: '/dashboard', show: isAuthenticated, external: false },
    { name: 'Whisper', path: '/whisper/', show: true, external: true },
  ];

  // Apply dark mode and persist preference
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  // Scroll behavior: hide when scrolling down, show on scroll-up or at the top,
  // and additionally hide (idle) after 2 seconds of no movement.
  useEffect(() => {
    let timeoutId;
    const handleScroll = () => {
      const currentScrollPos = window.scrollY;
      const scrollThreshold = 10;
      if (currentScrollPos < scrollPos - scrollThreshold || currentScrollPos === 0) {
        setIsVisible(true);
      } else if (currentScrollPos > scrollPos + scrollThreshold) {
        setIsVisible(false);
      }
      setScrollPos(currentScrollPos);
      if (currentScrollPos !== 0) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          setIsIdle(true);
          setIsVisible(false);
        }, 2000);
      } else {
        setIsIdle(false);
        setIsVisible(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timeoutId);
    };
  }, [scrollPos]);

  const gotoTop = () => {
    window.scrollTo(0, 0);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
  <>
    <nav
      className={`fixed top-2 left-0 right-0 mx-auto max-w-3xl rounded-2xl transition-transform duration-300 z-50 ${
        !isVisible ? 'transform -translate-y-full' : ''
      }`}
    >
      <div className="relative bg-opacity-10 dark:bg-opacity-10 dark:text-white px-4 py-3 rounded-2xl shadow-lg backdrop-blur-sm border border-ui-light-border dark:border-ui-dark-border">
        {/* detail fade overlays */}
        {/* <div className="absolute bottom-0 left-0 w-full h-1/5 pointer-events-none bg-gradient-to-t from-ui-light-detail/35 to-transparent dark:from-ui-dark-detail/35" /> */}

        <div className="flex justify-between items-center">
          {/* Left Section: Logo, Home Icon, and Desktop Nav Links */}
          <div className="flex items-center">
            <Link to="/" onClick={gotoTop} className="flex items-center">
              <div className="h-10 mr-3 relative">
                {isDarkMode ? (
                  <img
                    src="https://escola.cbpf.br/logos/cbpf-logo.png"
                    alt="CBPF Logo"
                    className="h-10 mr-3 object-contain"
                  />
                ) : (
                  <img
                    src="https://escola.cbpf.br/logos/cbpf-logo.png"
                    alt="CBPF Logo"
                    className="h-10 mr-3 object-contain filter invert"
                  />
                )}
              </div>
              <div className="flex items-center text-xl">
                {isDarkMode ? (
                  <HomeOutlinedIcon
                    fontSize="inherit"
                    className="align-middle mb-0.5 text-ui-dark-detail"
                  />
                ) : (
                  <HomeIcon
                    fontSize="inherit"
                    className="align-middle mb-0.5 text-ui-light-detail"
                  />
                )}
              </div>
            </Link>

            {/* Desktop Navigation Links */}
            <div className="hidden sm:flex space-x-4 ml-4">
              {navLinks
                .filter(link => link.show)
                .map(link =>
                  link.external ? (
                    <a
                      key={link.path}
                      href={link.path}
                      className="flex items-center px-3 py-2 text-sm font-medium transition-all duration-300 rounded-md hover:bg-secondary-dark"
                    >
                      {link.name}
                    </a>
                  ) : (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="flex items-center px-3 py-2 text-sm font-medium transition-all duration-300 rounded-md hover:bg-secondary-dark"
                    >
                      {link.name}
                    </Link>
                  )
                )}
            </div>
          </div>

          {/* Right Section: Mobile Menu Button, Theme Toggle, and User/Profile */}
          <div className="flex items-center space-x-2 z-10">
            {/* Mobile Menu Button */}
            <div className="sm:hidden">
              <button
                onClick={() => {
                  setIsMenuOpen(!isMenuOpen);
                  setIsProfileOpen(false);
                }}
                className="p-2 rounded-full hover:bg-secondary-dark transition-colors"
                aria-label="Toggle menu"
              >
                {isMenuOpen ? (
                  <svg
                    className="h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                ) : (
                  <svg
                    className="h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                )}
              </button>
            </div>

            {/* Theme Toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-full hover:bg-secondary-dark transition-colors"
              aria-label="Toggle dark mode"
            >
              {isDarkMode ? (
                <Brightness4Icon fontSize="small" className="align-middle mt-0.5" />
              ) : (
                <Brightness7Icon fontSize="small" className="align-middle mt-0.5" />
              )}
            </button>

            {/* User/Profile */}
            {!isAuthenticated ? (
              <Link
                to="/login"
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 dark:bg-primary-700 rounded-md shadow-sm hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors"
              >
                Sign in
              </Link>
            ) : (
              <div className="relative">
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className="flex items-center mt-1 justify-center w-8 h-8 text-white bg-primary-600 dark:bg-primary-700 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-offset-neutral-800"
                >
                  <span className="sr-only">Open user menu</span>
                  {user?.username?.charAt(0).toUpperCase() || 'U'}
                </button>
                {isProfileOpen && (
                  <div className="absolute right-0 z-10 w-48 py-1 mt-2 bg-secondary-light dark:bg-primary-dark bg-primary-100 rounded-md shadow-lg origin-top-right ring-1 dark:bg-primary-800 ring-black dark:ring-ui-dark-border">
                    <div className="px-4 py-2 text-sm text-secondary-dark dark:text-white border-b border-secondary-light dark:border-gray-700">
                      <div className="font-medium">{user?.full_name || user?.username}</div>
                      <div className="text-xs truncate">{user?.email}</div>
                    </div>
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-sm hover:bg-secondary-dark dark:hover:bg-gray-800 transition-colors"
                      onClick={() => setIsProfileOpen(false)}
                    >
                      Profile Settings
                    </Link>
                    <Link
                      to="/queries/history"
                      className="block px-4 py-2 text-sm hover:bg-secondary-dark dark:hover:bg-gray-800 transition-colors"
                      onClick={() => setIsProfileOpen(false)}
                    >
                      My Queries
                    </Link>
                    <Link
                      to="/news"
                      className="block px-4 py-2 text-sm hover:bg-secondary-dark dark:hover:bg-gray-800 transition-colors"
                      onClick={() => setIsProfileOpen(false)}
                    >
                      News
                    </Link>
                    <Link
                      to="/shared-notes"
                      className="block px-4 py-2 text-sm hover:bg-secondary-dark dark:hover:bg-gray-800 transition-colors"
                      onClick={() => setIsProfileOpen(false)}
                    >
                      Shared Notes
                    </Link>
                    {hasAdminAccess && (
                      <Link
                        to="/admin"
                        className="block px-4 py-2 text-sm hover:bg-secondary-dark dark:hover:bg-gray-800 transition-colors"
                        onClick={() => setIsProfileOpen(false)}
                      >
                        Admin Dashboard
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsProfileOpen(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm hover:bg-secondary-dark dark:hover:bg-gray-800 transition-colors"
                    >
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {isMenuOpen && (
        <div className="sm:hidden bg-secondary-light dark:bg-primary-dark bg-opacity-90 dark:bg-opacity-90 dark:text-white px-4 py-3 mt-2 rounded-2xl shadow-lg backdrop-blur-sm">
          <div className="space-y-2">
            {navLinks
              .filter(link => link.show)
              .map(link =>
                link.external ? (
                  <a
                    key={link.path}
                    href={link.path}
                    onClick={() => setIsMenuOpen(false)}
                    className="block flex items-center px-3 py-2 text-base font-medium transition-all duration-300 rounded-md hover:bg-secondary-dark"
                  >
                    {link.name}
                  </a>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMenuOpen(false)}
                    className="block flex items-center px-3 py-2 text-base font-medium transition-all duration-300 rounded-md hover:bg-secondary-dark"
                  >
                    {link.name}
                  </Link>
                )
              )}
          </div>

          {isAuthenticated && (
            <div className="pt-4 pb-3 border-t border-secondary-light dark:border-gray-700">
              <div className="flex items-center px-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center w-10 h-10 text-white bg-primary-600 dark:bg-primary-700 rounded-full">
                    {user?.username?.charAt(0).toUpperCase() || 'U'}
                  </div>
                </div>
                <div className="ml-3">
                  <div className="text-base font-medium text-secondary-dark dark:text-white">
                    {user?.full_name || user?.username}
                  </div>
                  <div className="text-sm font-medium text-gray-500">{user?.email}</div>
                </div>
              </div>

              <div className="mt-3 space-y-1">
                <Link
                  to="/profile"
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-4 py-2 text-base font-medium hover:bg-secondary-dark dark:hover:bg-gray-800 transition-colors"
                >
                  Profile Settings
                </Link>
                <Link
                  to="/queries/history"
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-4 py-2 text-base font-medium hover:bg-secondary-dark dark:hover:bg-gray-800 transition-colors"
                >
                  My Queries
                </Link>
                <Link
                  to="/news"
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-4 py-2 text-base font-medium hover:bg-secondary-dark dark:hover:bg-gray-800 transition-colors"
                >
                  News
                </Link>
                {hasAdminAccess && (
                  <Link
                    to="/admin"
                    onClick={() => setIsMenuOpen(false)}
                    className="block px-4 py-2 text-base font-medium hover:bg-secondary-dark dark:hover:bg-gray-800 transition-colors"
                  >
                    Admin Dashboard
                  </Link>
                )}
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-base font-medium hover:bg-secondary-dark dark:hover:bg-gray-800 transition-colors"
                >
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </nav>
  </>
);
}